import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom/client';
import axios from 'axios';
import SessionExpiredModal from './SessionExpiredModal';
import './index.css';
import { BrowserRouter as Router, Route, Routes, Navigate, useNavigate, useLocation } from 'react-router-dom';
import AppSelector from './App';
import reportWebVitals from './reportWebVitals';
import Spinner from './spinner/spinner';
// import ShopModule from './shop-module/ShopModule';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import 'bootstrap-icons/font/bootstrap-icons.css';
import "./i18n";
import PncNew from '../src/pncNew/pncNew.js';
import ShopManualNew from '../src/shopManual/pncNew.js';
import FusoLoginPage from './login/loginNew.js';
import AdminDashboard from './admin/AdminDashboard.js';

// Enhanced authentication check function
const isUserAuthenticated = () => {
  const loginStatus = localStorage.getItem('login');
  const authToken = localStorage.getItem('authToken');
  return loginStatus === 'true' && authToken;
};

// Simpler authentication check
const isUserAuthenticatedSimple = () => {
  const loginStatus = localStorage.getItem('login');
  return loginStatus === 'true';
};

// Environment check
const isLocalEnvironment = () => {
  return window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname.includes('local') ||
    process.env.NODE_ENV === 'development';
};

// Enhanced logout function with detailed logging - ONLY for URL manipulation
const handleLogout = (reason = 'unauthorized_access', additionalInfo = '') => {
  console.warn(`🚨 SECURITY BREACH DETECTED 🚨`);
  console.warn(`Reason: ${reason}`);
  console.warn(`Additional Info: ${additionalInfo}`);
  console.warn(`Timestamp: ${new Date().toISOString()}`);
  console.warn(`URL at breach: ${window.location.href}`);

  // Clear all storage
  localStorage.clear();
  sessionStorage.clear();

  // Store logout reason for audit trail
  sessionStorage.setItem('logout_reason', reason);
  sessionStorage.setItem('logout_timestamp', Date.now().toString());

  // Immediate redirect with page reload to ensure clean state
  window.location.replace("/login");
};

// Helper function to determine which module a URL belongs to
const getCurrentModule = (url) => {
  if (url.includes('/pnc')) return 'pnc';
  if (url.includes('/shop-manual')) return 'shop-manual';
  if (url.includes('/ascent')) return 'ascent';
  if (url.includes('/admin')) return 'admin';
  return null;
};

// Get all protected routes
const getProtectedRoutes = () => ['/shop-manual', '/pnc', '/ascent', '/admin'];

// Check if route is protected
const isProtectedRoute = (path) => {
  return getProtectedRoutes().some(route => path.includes(route));
};

// URL Security Monitor - ONLY for manual URL manipulation
const useURLSecurityMonitor = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const lastLocationRef = useRef(location.pathname + location.search);
  const allowedNavigationRef = useRef(false);
  const isInitialLoadRef = useRef(true);
  const sessionStartTimeRef = useRef(null);
  const navigationCountRef = useRef(0);
  const lastNavigationTimeRef = useRef(0);

  useEffect(() => {
    const currentURL = location.pathname + location.search;
    const previousURL = lastLocationRef.current;
    const isLocal = isLocalEnvironment();
    const isAuth = isUserAuthenticatedSimple();
    const isCurrentProtected = isProtectedRoute(location.pathname);
    const now = Date.now();

    // Handle initial load
    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;

      // Check if this is a page refresh vs direct access
      const storedURL = sessionStorage.getItem('last_known_url');
      const isPageRefresh = storedURL && storedURL === currentURL;

      // If it's a page refresh of a protected route and user is authenticated, allow it
      if (isPageRefresh && isCurrentProtected && isAuth) {
        lastLocationRef.current = currentURL;
        sessionStartTimeRef.current = now;
        sessionStorage.setItem('last_refresh_time', now.toString());
        return;
      }

      // If user directly accesses ANY protected route (not a refresh), immediate logout
      if (isCurrentProtected && !isPageRefresh) {
        console.warn('🚨 DIRECT ACCESS TO PROTECTED ROUTE BLOCKED 🚨');
        console.warn(`Attempted direct access to: ${currentURL}`);
        handleLogout('direct_protected_access', `Attempted direct access to: ${currentURL}`);
        return;
      }

      // Store the initial URL
      sessionStorage.setItem('last_known_url', currentURL);
      lastLocationRef.current = currentURL;
      sessionStartTimeRef.current = now;
      return;
    }

    // Development environment handling
    if (isLocal) {
      // Allow authenticated users to navigate between protected routes in development
      if (isAuth && isCurrentProtected && isProtectedRoute(previousURL)) {
        sessionStorage.setItem('last_known_url', currentURL);
        lastLocationRef.current = currentURL;
        return;
      }

      // Still block direct access to protected routes even in development
      if (!isAuth && isCurrentProtected) {
        console.warn('🚨 DEV: Unauthenticated access blocked');
        handleLogout('dev_unauthenticated_access', `Attempted access to: ${currentURL}`);
        return;
      }

      // Allow other development navigation but log it
      sessionStorage.setItem('last_known_url', currentURL);
      lastLocationRef.current = currentURL;
      return;
    }

    // Skip if not authenticated (will be handled by ProtectedRoute)
    if (!isAuth) {
      sessionStorage.setItem('last_known_url', currentURL);
      lastLocationRef.current = currentURL;
      return;
    }

    // PRODUCTION: Check for URL changes vs page refresh
    if (currentURL !== previousURL) {
      const timeSinceLastNav = now - lastNavigationTimeRef.current;
      const navigationInProgress = sessionStorage.getItem('navigation_in_progress');
      const legitimateNavigation = sessionStorage.getItem('legitimate_navigation');
      const appInitiatedNavigation = sessionStorage.getItem('app_initiated_navigation');
      const isAllowedNavigation = allowedNavigationRef.current;

      // Check if this might be a page refresh
      const storedURL = sessionStorage.getItem('last_known_url');
      const isPageRefresh = storedURL && storedURL === currentURL;
      const lastRefreshTime = sessionStorage.getItem('last_refresh_time');
      const timeSinceRefresh = lastRefreshTime ? now - parseInt(lastRefreshTime) : Infinity;

      // Extended grace period for legitimate navigation
      const isWithinGracePeriod = (timeSinceLastNav < 200 && (navigationInProgress || appInitiatedNavigation)) ||
        (timeSinceLastNav < 2000 && legitimateNavigation);

      // If this is a page refresh, allow it
      if (isPageRefresh && timeSinceRefresh > 1000) {
        sessionStorage.setItem('last_refresh_time', now.toString());
        sessionStorage.setItem('last_known_url', currentURL);
        lastLocationRef.current = currentURL;
        return;
      }

      // ZERO TOLERANCE: If ANY navigation happens without explicit app permission and it's not a refresh
      if (!isAllowedNavigation && !navigationInProgress && !legitimateNavigation &&
        !appInitiatedNavigation && !isWithinGracePeriod && !isPageRefresh) {
        console.warn('🚨 UNAUTHORIZED URL MANIPULATION DETECTED 🚨');
        console.warn('ZERO TOLERANCE POLICY ACTIVATED');
        console.warn(`This appears to be manual URL manipulation, not a page refresh`);
        handleLogout('unauthorized_url_manipulation',
          `From: ${previousURL} To: ${currentURL}, Time: ${timeSinceLastNav}ms, NotRefresh: ${!isPageRefresh}`);
        return;
      }

      // Additional check: Rapid navigation attempts (potential manipulation)
      navigationCountRef.current++;
      if (navigationCountRef.current > 3 && timeSinceLastNav < 1000 && !isPageRefresh) {
        console.warn('🚨 RAPID NAVIGATION DETECTED - SUSPICIOUS ACTIVITY 🚨');
        handleLogout('rapid_navigation_detected',
          `Navigation count: ${navigationCountRef.current}, Time: ${timeSinceLastNav}ms`);
        return;
      }

      // Cross-module navigation check (stricter) - but allow if it's app-initiated
      if (isCurrentProtected && isProtectedRoute(previousURL) && !isPageRefresh) {
        const currentModule = getCurrentModule(location.pathname);
        const previousModule = getCurrentModule(previousURL);

        if (currentModule !== previousModule && currentModule && previousModule) {
          // Allow cross-module navigation if it's app-initiated
          if (!appInitiatedNavigation && !legitimateNavigation) {
            console.warn('🚨 UNAUTHORIZED CROSS-MODULE NAVIGATION BLOCKED 🚨');
            handleLogout('unauthorized_cross_module_navigation',
              `From: ${previousModule} To: ${currentModule}`);
            return;
          } else {
          }
        }
      }

      lastNavigationTimeRef.current = now;
    }

    // Update tracking
    sessionStorage.setItem('last_known_url', currentURL);
    lastLocationRef.current = currentURL;
    sessionStorage.setItem('last_url_update_time', now.toString());
    allowedNavigationRef.current = false; // Reset immediately after check

    // Clean up navigation flags after successful navigation
    if (sessionStorage.getItem('navigation_in_progress')) {
      setTimeout(() => {
        sessionStorage.removeItem('navigation_in_progress');
      }, 100);
    }

    if (sessionStorage.getItem('app_initiated_navigation')) {
      setTimeout(() => {
        sessionStorage.removeItem('app_initiated_navigation');
      }, 300);
    }
  }, [location]);

  // Minimal browser event monitoring - ONLY for cleanup, no logout triggers
  useEffect(() => {
    const isLocal = isLocalEnvironment();

    // Basic cleanup handler for page unload
    const handleBeforeUnload = (e) => {
      // Clean up flags but preserve URL tracking for refresh detection
      sessionStorage.removeItem('navigation_in_progress');
      sessionStorage.removeItem('app_initiated_navigation');
      sessionStorage.removeItem('page_hidden_time');
      // Keep 'last_known_url' for refresh detection
      // Keep modal-related flags for potential refresh scenarios
    };

    // Allow page refresh without any restrictions
    const handleKeyDown = (e) => {
      const refreshKeys = [
        'F5',
        ...(e.ctrlKey ? ['r', 'R'] : []),
        ...(e.ctrlKey && e.shiftKey ? ['r', 'R'] : []),
      ];

      // Just log refresh keys, no blocking
      if (refreshKeys.includes(e.key)) {
        sessionStorage.setItem('refresh_initiated', 'true');
        sessionStorage.setItem('last_refresh_time', Date.now().toString());
      }
    };

    // Add minimal event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, []);

  // Secure navigate function with strict validation
  const secureNavigate = (to, options = {}) => {

    const targetRoute = typeof to === 'string' ? to : to.pathname || '';
    const allowedRoutes = ['/login', '/shop-manual', '/pnc', '/ascent', '/admin'];

    if (!allowedRoutes.includes(targetRoute)) {
      console.warn('🚨 INVALID ROUTE BLOCKED 🚨');
      handleLogout('invalid_route_attempt', `Attempted route: ${targetRoute}`);
      return;
    }

    // Set security flags
    allowedNavigationRef.current = true;
    sessionStorage.setItem('navigation_in_progress', 'true');
    sessionStorage.setItem('navigation_timestamp', Date.now().toString());
    sessionStorage.setItem('navigation_target', targetRoute);
    sessionStorage.setItem('app_initiated_navigation', 'true');

    setTimeout(() => {
      try {
        navigate(to, options);
        // Update the stored URL immediately after navigation
        setTimeout(() => {
          sessionStorage.setItem('last_known_url', targetRoute);
        }, 50);
      } catch (error) {
        console.error('❌ Navigation error:', error);
        handleLogout('navigation_error', `Error: ${error.message}`);
      }
    }, 10);

    setTimeout(() => {
      sessionStorage.removeItem('navigation_in_progress');
      sessionStorage.removeItem('app_initiated_navigation');
      allowedNavigationRef.current = false;
    }, 300);
  };

  return secureNavigate;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const [isChecking, setIsChecking] = useState(true);
  const [authValidated, setAuthValidated] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const performAuthCheck = () => {
      const isAuthenticated = isUserAuthenticatedSimple();
      const currentPath = location.pathname;
      const isCurrentProtected = isProtectedRoute(currentPath);

      if (!isAuthenticated) {
        console.warn('🚨 UNAUTHENTICATED ACCESS BLOCKED 🚨');
        localStorage.clear();
        sessionStorage.clear();
        navigate('/login', { replace: true });
        return;
      }

      if (isCurrentProtected) {
        // Basic session validation
        const sessionEstablished = sessionStorage.getItem('session_established');
        const loginTimestamp = sessionStorage.getItem('login_timestamp');
        const currentTime = Date.now();

        // Check session age (logout after 8 hours)
        if (loginTimestamp && (currentTime - parseInt(loginTimestamp)) > 8 * 60 * 60 * 1000) {
          console.warn('🚨 SESSION EXPIRED 🚨');
          handleLogout('session_expired', `Session age: ${(currentTime - parseInt(loginTimestamp)) / 1000}s`);
          return;
        }

        // If no session established but user is authenticated, establish it now
        if (!sessionEstablished && isAuthenticated) {
          sessionStorage.setItem('session_established', 'true');
          sessionStorage.setItem('legitimate_session', 'true');
          sessionStorage.setItem('login_timestamp', currentTime.toString());
        }

        // Validate current URL against allowed patterns
        const allowedPatterns = [
          /^\/shop-manual$/,
          /^\/pnc$/,
          /^\/ascent$/,
          /^\/admin$/
        ];

        const isValidURL = allowedPatterns.some(pattern => pattern.test(currentPath));
        if (!isValidURL) {
          console.warn('🚨 INVALID URL PATTERN DETECTED 🚨');
          handleLogout('invalid_url_pattern', `URL: ${currentPath}`);
          return;
        }
      }

      setAuthValidated(true);
      setIsChecking(false);
    };

    const timeoutId = setTimeout(performAuthCheck, 25);
    return () => clearTimeout(timeoutId);
  }, [navigate, location.pathname]);

  if (isChecking || !authValidated) {
    return <Spinner />;
  }

  const isAuthenticated = isUserAuthenticatedSimple();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Mark legitimate session establishment
  sessionStorage.setItem('session_established', 'true');
  sessionStorage.setItem('legitimate_session', 'true');
  sessionStorage.setItem('route_access_time', Date.now().toString());
  sessionStorage.setItem('last_known_url', location.pathname + location.search);

  return children;
};

// Auth Provider Component
const AuthProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Enable URL security monitoring
  useURLSecurityMonitor();

  useEffect(() => {
    const currentPath = location.pathname;
    const urlParams = new URLSearchParams(location.search);
    const isAuthenticated = isUserAuthenticatedSimple();

    // Clear any previous session markers when accessing login
    if (currentPath === '/login') {
      sessionStorage.clear();
      localStorage.removeItem('login'); // Clear login status on login page
    }

    // Handle initial routing
    if (currentPath === '/') {
      navigate('/login', { replace: true });
    } else if (isProtectedRoute(currentPath)) {
      // Any direct access to protected routes without proper authentication flow
      if (!isAuthenticated) {
        console.warn('🚨 UNAUTHENTICATED PROTECTED ROUTE ACCESS 🚨');
        navigate('/login', { replace: true });
      }
    }

    setIsLoading(false);
  }, [location, navigate]);

  if (isLoading) {
    return <Spinner />;
  }

  return children;
};

// Generate cryptographically secure random string
export const generateSecureRandomString = (length = 32) => {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

// Generate cryptographically secure user ID
export const generateSecureUserId = (email) => {
  // Use a combination of email hash and random data for better security
  const emailHash = Array.from(new TextEncoder().encode(email))
    .map(byte => byte.toString(16).padStart(2, '0'))
    .join('')
    .substring(0, 16);
  const randomPart = generateSecureRandomString(8);
  return (emailHash + randomPart).substring(0, 24);
};

// Enhanced Login Success Handler with comprehensive session setup
export const handleLoginSuccess = (userInfo = {}) => {
  const timestamp = Date.now();

  // Set authentication markers
  localStorage.setItem('login', 'true');
  localStorage.setItem('authToken', userInfo.token || 'auth-token-' + generateSecureRandomString(32));

  // Set comprehensive session markers
  sessionStorage.setItem('session_established', 'true');
  sessionStorage.setItem('legitimate_session', 'true');
  sessionStorage.setItem('came_from_login', 'true');
  sessionStorage.setItem('login_timestamp', timestamp.toString());
  sessionStorage.setItem('session_id', 'session-' + timestamp + '-' + generateSecureRandomString(16));
  sessionStorage.setItem('user_agent_hash', generateSecureRandomString(20));
};

// Enhanced Logout Handler for manual logout
export const handleManualLogout = () => {
  handleLogout('manual_logout', 'User initiated logout');
};

const App = () => {
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    const onSessionExpired = () => setSessionExpired(true);
    window.addEventListener('session-expired', onSessionExpired);
    return () => window.removeEventListener('session-expired', onSessionExpired);
  }, []);

  return (
    <>
      <SessionExpiredModal
        isOpen={sessionExpired}
        onConfirm={() => {
          localStorage.clear();
          sessionStorage.clear();
          window.location.href = '/oauth2/sign_out?rd=%2Foauth2%2Fstart%3Frd%3D%2Flogin';
        }}
      />
      <Router>
        <AuthProvider>
          <Routes>
        {/* Root path always goes to login */}
        <Route path="/" element={<Navigate to="/login" replace />} />

        {/* Public route */}
        <Route path="/login" element={<FusoLoginPage />} />

        {/* Protected routes */}
        <Route
          path="/shop-manual"
          element={
            <ProtectedRoute>
              <ShopManualNew selectedModule="Shop Manual" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ascent"
          element={
            <ProtectedRoute>
              <AppSelector selectedModule="Ascent" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pnc"
          element={
            <ProtectedRoute>
              <PncNew selectedModule="PNC" />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />

        {/* Catch all other routes - redirect to login */}
        <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </AuthProvider>
      </Router>
    </>
  );
};

// OAuth2 proxy session expiry — fires once, shows modal, redirects to login
let sessionExpiredHandled = false;
const handleSessionExpired = () => {
  if (sessionExpiredHandled) return;
  sessionExpiredHandled = true;
  window.dispatchEvent(new CustomEvent('session-expired'));
};

const originalFetch = window.fetch;
window.fetch = async (...args) => {
  const response = await originalFetch(...args);
  if ((response.headers.get('content-type') || '').includes('text/html')) {
    const url = typeof args[0] === 'string' ? args[0] : (args[0] instanceof Request ? args[0].url : '');
    // API routes (/v1/) should never return HTML — treat as endpoint error, not OAuth expiry.
    // OAuth expiry for API calls is caught by the Axios interceptor below.
    if (url.includes('/v1/')) {
      throw new Error('API endpoint returned HTML — backend may be offline or route does not exist');
    }
    handleSessionExpired();
    return new Promise(() => {});
  }
  return response;
};

axios.interceptors.response.use((response) => {
  if ((response.headers['content-type'] || '').includes('text/html')) {
    handleSessionExpired();
    return new Promise(() => {});
  }
  return response;
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<App />);

reportWebVitals();